import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "@/store"
import {
  fetchLiveWorkflows,
  selectLiveWorkflows,
  cancelWorkflowInstance,
  reactivateWorkflowInstance,
  cancelTask,
  reactivateTask,
} from "../../store/liveWorkflowSlice"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import {
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  XCircle,
  PlayCircle,
} from "lucide-react"
import { toast } from "sonner"
import { WorkflowConfirmationDialog } from "../components/WorkflowConfirmationDialog"
import type {
  WorkflowInstanceDto,
  WorkflowTaskInstanceDto,
} from "../../dtos/liveWorkflowDto"

export default function WorkflowDetailsPage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const liveInstances = useSelector(selectLiveWorkflows)
  const { isLoading } = useSelector((state: RootState) => state.liveWorkflows)

  const [expandedInstances, setExpandedInstances] = useState<Set<string>>(
    new Set(),
  )
  const [cancelDialog, setCancelDialog] = useState<{
    open: boolean
    type: "instance" | "task" | "reactivate-instance" | "reactivate-task"
    instanceId?: string
    taskId?: string
    instanceName?: string
    taskTitle?: string
  }>({
    open: false,
    type: "instance",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    dispatch(fetchLiveWorkflows({ includeTasks: true }))
  }, [dispatch])

  const instances = liveInstances.filter(
    (inst) => inst.workflowId === workflowId,
  )
  const template = instances[0]

  const toggleInstance = (instanceId: string) => {
    setExpandedInstances((prev) => {
      const next = new Set(prev)
      if (next.has(instanceId)) {
        next.delete(instanceId)
      } else {
        next.add(instanceId)
      }
      return next
    })
  }

  const getTaskStatusBadge = (status: string) => {
    switch (status) {
      case "Completed":
        return <Badge className="bg-green-100 text-green-700">Completed</Badge>
      case "Declined":
        return <Badge className="bg-red-100 text-red-700">Declined</Badge>
      case "Pending":
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
      case "Expired":
        return <Badge className="bg-muted text-foreground">Cancelled</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const getInstanceStatusBadge = (status: string) => {
    switch (status) {
      case "InProgress":
        return <Badge className="bg-blue-100 text-blue-700">In Progress</Badge>
      case "Completed":
        return <Badge className="bg-green-100 text-green-700">Completed</Badge>
      case "Cancelled":
        return <Badge className="bg-red-100 text-red-700">Cancelled</Badge>
      case "Paused":
        return <Badge className="bg-yellow-100 text-yellow-700">Paused</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const getTaskCounts = (instance: WorkflowInstanceDto) => {
    const completed = instance.tasks.filter(
      (t) => t.status === "Completed",
    ).length
    const declined = instance.tasks.filter(
      (t) => t.status === "Declined",
    ).length
    const cancelled = instance.tasks.filter(
      (t) => t.status === "Expired",
    ).length
    return { completed, declined, cancelled }
  }

  const handleCancelInstance = async (
    instanceId: string,
    instanceName: string,
  ) => {
    setCancelDialog({
      open: true,
      type: "instance",
      instanceId,
      instanceName,
    })
  }

  const handleReactivateInstance = async (
    instanceId: string,
    instanceName: string,
  ) => {
    setCancelDialog({
      open: true,
      type: "reactivate-instance",
      instanceId,
      instanceName,
    })
  }

  const handleCancelTask = async (
    instanceId: string,
    taskId: string,
    taskTitle: string,
  ) => {
    setCancelDialog({
      open: true,
      type: "task",
      instanceId,
      taskId,
      taskTitle,
    })
  }

  const handleReactivateTask = async (
    instanceId: string,
    taskId: string,
    taskTitle: string,
  ) => {
    setCancelDialog({
      open: true,
      type: "reactivate-task",
      instanceId,
      taskId,
      taskTitle,
    })
  }

  const handleConfirmCancel = async () => {
    if (!cancelDialog.instanceId) return

    setIsSubmitting(true)
    try {
      if (cancelDialog.type === "instance") {
        await dispatch(
          cancelWorkflowInstance({ instanceId: cancelDialog.instanceId }),
        ).unwrap()
        toast.success("Workflow instance cancelled")
      } else if (cancelDialog.type === "task" && cancelDialog.taskId) {
        await dispatch(
          cancelTask({
            instanceId: cancelDialog.instanceId,
            taskId: cancelDialog.taskId,
          }),
        ).unwrap()
        toast.success("Task cancelled")
      } else if (cancelDialog.type === "reactivate-instance") {
        await dispatch(
          reactivateWorkflowInstance(cancelDialog.instanceId),
        ).unwrap()
        toast.success("Workflow instance reactivated")
      } else if (
        cancelDialog.type === "reactivate-task" &&
        cancelDialog.taskId
      ) {
        await dispatch(
          reactivateTask({
            instanceId: cancelDialog.instanceId,
            taskId: cancelDialog.taskId,
          }),
        ).unwrap()
        toast.success("Task reactivated")
      }

      setCancelDialog({ open: false, type: "instance" })
      await dispatch(fetchLiveWorkflows({ includeTasks: true })).unwrap()
    } catch (error) {
      toast.error(
        (error as string) ||
          (cancelDialog.type.includes("reactivate")
            ? cancelDialog.type === "reactivate-instance"
              ? "Failed to reactivate workflow instance"
              : "Failed to reactivate task"
            : cancelDialog.type === "instance"
              ? "Failed to cancel workflow instance"
              : "Failed to cancel task"),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading && instances.length === 0) {
    return (
      <div className="p-4">
        <div className="text-center py-12">Loading workflow details...</div>
      </div>
    )
  }

  if (!template) {
    return (
      <div className="p-4">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Workflow not found</p>
          <Button
            variant="outline"
            onClick={() => navigate("/onboarding/workflows/live")}
            className="mt-4"
          >
            Back to Live Workflows
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header Section */}
      <div className="bg-card rounded-lg border border-border p-8 shadow-sm">
        <Button
          variant="ghost"
          onClick={() => navigate("/onboarding/workflows/live")}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Live Workflows
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {template.workflowName}
            </h1>
            {template.workflowDescription && (
              <p className="text-muted-foreground mt-2">
                {template.workflowDescription}
              </p>
            )}
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-700">
                {instances.length} Instance{instances.length !== 1 ? "s" : ""}
              </Badge>
            </div>
          </div>
        </div>

        {/* Batch Information */}
        {instances.some((inst) => inst.batchId) && (
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="text-sm font-semibold text-foreground mb-3">
              Batch Groups
            </h3>
            <div className="space-y-2">
              {Array.from(
                new Map(
                  instances
                    .filter((inst) => inst.batchId)
                    .map((inst) => [
                      inst.batchId,
                      {
                        batchId: inst.batchId,
                        batchName: inst.batchName,
                        count: instances.filter(
                          (i) => i.batchId === inst.batchId,
                        ).length,
                      },
                    ]),
                ).values(),
              ).map((batch) => (
                <div
                  key={batch.batchId}
                  className="flex items-center justify-between bg-muted rounded-lg px-4 py-2"
                >
                  <div>
                    <p className="font-medium text-foreground">
                      {batch.batchName || "Unnamed Batch"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {batch.count} instance{batch.count !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <Badge variant="outline">{batch.batchId?.slice(0, 8)}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Instances List */}
      <div className="space-y-4">
        {instances.map((instance) => {
          const isExpanded = expandedInstances.has(instance.id)
          const counts = getTaskCounts(instance)
          const contextLabel = instance.assignedToName || "Standalone"

          return (
            <div
              key={instance.id}
              className="bg-card rounded-lg border border-border shadow-sm overflow-hidden"
            >
              {/* Instance Header */}
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div
                    className="flex items-center gap-4 flex-1 cursor-pointer hover:bg-muted transition-colors -m-6 p-6"
                    onClick={() => toggleInstance(instance.id)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-foreground">
                          {contextLabel}
                        </h3>
                        {instance.referenceNumber && (
                          <span className="text-xs text-muted-foreground font-mono">
                            {instance.referenceNumber}
                          </span>
                        )}
                        {getInstanceStatusBadge(instance.status)}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span>
                          Started:{" "}
                          {new Date(
                            instance.startedAt || "",
                          ).toLocaleDateString()}
                        </span>
                        <span>•</span>
                        <span className="text-green-700 font-medium">
                          {counts.completed} Completed
                        </span>
                        <span className="text-red-700 font-medium">
                          {counts.declined} Declined
                        </span>
                        <span className="text-foreground font-medium">
                          {counts.cancelled} Cancelled
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {instance.tasks.length} Task
                      {instance.tasks.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {instance.status === "Cancelled" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleReactivateInstance(instance.id, contextLabel)
                        }
                      >
                        <PlayCircle className="w-4 h-4 mr-2" />
                        Reactivate
                      </Button>
                    ) : instance.status === "InProgress" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleCancelInstance(instance.id, contextLabel)
                        }
                        className="text-red-600 hover:text-red-700"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Expanded Task Details */}
              {isExpanded && (
                <div className="border-t border-border bg-muted">
                  <div className="p-6">
                    <h4 className="font-semibold text-foreground mb-4">Tasks</h4>
                    <div className="space-y-3">
                      {instance.tasks.map((task: WorkflowTaskInstanceDto) => (
                        <div
                          key={task.id}
                          className="bg-card rounded-lg border border-border p-4"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h5 className="font-medium text-foreground">
                                  {task.title}
                                </h5>
                                {getTaskStatusBadge(task.status)}
                              </div>
                              {task.description && (
                                <p className="text-sm text-muted-foreground mb-3">
                                  {task.description}
                                </p>
                              )}
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="text-muted-foreground font-medium">
                                    Assignee:
                                  </span>
                                  <span className="text-foreground">
                                    {task.assignedToEmail}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {task.assigneeType}
                                  </Badge>
                                </div>
                                {task.dueDate && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground font-medium">
                                      Due Date:
                                    </span>
                                    <span className="text-foreground">
                                      {new Date(
                                        task.dueDate,
                                      ).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                                {task.completedAt && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground font-medium">
                                      Completed:
                                    </span>
                                    <span className="text-foreground">
                                      {new Date(
                                        task.completedAt,
                                      ).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                                {task.declinedAt && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground font-medium">
                                      Declined:
                                    </span>
                                    <span className="text-foreground">
                                      {new Date(
                                        task.declinedAt,
                                      ).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                                {task.comments.length > 0 && (
                                  <div className="mt-3 space-y-2">
                                    {task.comments.map((comment) => (
                                      <div
                                        key={comment.id}
                                        className={`p-3 rounded border text-sm ${
                                          comment.commentType === 1
                                            ? "bg-blue-50 border-blue-200"
                                            : comment.commentType === 2
                                              ? "bg-red-50 border-red-200"
                                              : "bg-muted border-border"
                                        }`}
                                      >
                                        <span className="font-medium text-muted-foreground block mb-1">
                                          {comment.commentType === 1
                                            ? "Completion Notes"
                                            : comment.commentType === 2
                                              ? "Decline Reason"
                                              : comment.authorEmail}
                                        </span>
                                        <p className="text-foreground">
                                          {comment.text}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              {task.status === "Expired" ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    handleReactivateTask(
                                      instance.id,
                                      task.id,
                                      task.title,
                                    )
                                  }
                                >
                                  <PlayCircle className="w-4 h-4 mr-2" />
                                  Reactivate
                                </Button>
                              ) : task.status === "Pending" ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    handleCancelTask(
                                      instance.id,
                                      task.id,
                                      task.title,
                                    )
                                  }
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Cancel
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Confirmation Dialog */}
      <WorkflowConfirmationDialog
        open={cancelDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setCancelDialog({ open: false, type: "instance" })
          }
        }}
        onConfirm={handleConfirmCancel}
        title={
          cancelDialog.type === "instance"
            ? "Cancel Workflow Instance"
            : cancelDialog.type === "task"
              ? "Cancel Task"
              : cancelDialog.type === "reactivate-instance"
                ? "Reactivate Workflow Instance"
                : "Reactivate Task"
        }
        description={
          cancelDialog.type === "instance"
            ? `Are you sure you want to cancel "${cancelDialog.instanceName}"? This will cancel all pending tasks and cannot be undone.`
            : cancelDialog.type === "task"
              ? `Are you sure you want to cancel "${cancelDialog.taskTitle}"? This action cannot be undone.`
              : cancelDialog.type === "reactivate-instance"
                ? `Are you sure you want to reactivate "${cancelDialog.instanceName}"? This will make the workflow active again.`
                : `Are you sure you want to reactivate "${cancelDialog.taskTitle}"? This will make the task active again.`
        }
        confirmText={cancelDialog.type.includes("cancel") ? "Cancel" : "Cancel"}
        cancelText="Back"
        isSubmitting={isSubmitting}
        variant={cancelDialog.type.includes("cancel") ? "danger" : "warning"}
      />
    </div>
  )
}
